import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";

interface DatePickerProps {
  value: Date | null;
  onChange?: (date: Date | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  clearable?: boolean;
  fromYear?: number;
  toYear?: number;
  maxDate?: Date;
  /** When true, the selected date is visible but cannot be changed. */
  disabled?: boolean;
  "aria-invalid"?: boolean;
}

export function DatePicker({
  value,
  onChange,
  onBlur,
  placeholder = "Pick a date",
  clearable = true,
  fromYear = 2000,
  toYear = 2050,
  maxDate,
  disabled = false,
  "aria-invalid": ariaInvalid,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(date ?? null);
    setOpen(false); // Close the popover when a date is selected
    onBlur?.();
  };

  const handleClearDate = () => {
    onChange?.(null);
    setOpen(false); // Close the popover when date is cleared
    onBlur?.();
  };

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
      modal={false}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-empty={!value}
          className="w-full justify-start text-left font-normal"
          disabled={disabled}
          onClick={() => onBlur?.()}
          onBlur={() => onBlur?.()}
          aria-invalid={ariaInvalid}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (
            <span>{format(value, "PPP")}</span>
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        container={typeof document !== "undefined" ? document.body : undefined}
      >
        <Calendar
          mode="single"
          selected={value ?? undefined}
          defaultMonth={value ?? undefined}
          onSelect={handleDateSelect}
          autoFocus
          startMonth={new Date(fromYear, 0)}
          endMonth={new Date(toYear, 0)}
          disabled={(date) => (maxDate ? date > maxDate : false)}
        />
        {clearable && !disabled && value && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleClearDate}
            >
              <X className="mr-2 h-4 w-4" />
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
